import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI } from '@/lib/adminApi';
import { ArrowLeft, Mail, Calendar, CheckCircle, Package, Bell } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminUserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    adminAPI.getUser(userId).then(r => {
      setData(r.data);
      setNotes(r.data.user.admin_notes || '');
    }).catch(() => toast.error('Failed to load user')).finally(() => setLoading(false));
  }, [userId]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await adminAPI.updateUser(userId, { notes });
      toast.success('Notes saved');
    } catch { toast.error('Save failed'); }
    finally { setSavingNotes(false); }
  };

  if (loading) return <div className="p-10 text-purple-200/50">Loading…</div>;
  if (!data) return <div className="p-10 text-red-300">User not found.</div>;

  const u = data.user;

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-user-detail">
      <button onClick={() => navigate('/skyportal/users')} className="flex items-center gap-2 text-purple-200/60 hover:text-white text-sm mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Users
      </button>

      <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6 mb-6">
        <div className="flex items-start gap-5">
          <div className="w-16 h-16 rounded-2xl bg-purple-500/20 border border-purple-400/30 flex items-center justify-center text-purple-200 text-2xl font-bold">
            {(u.name || u.email || '?')[0]?.toUpperCase()}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white">{u.name || 'Unknown'}</h1>
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-purple-200/60">
              <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {u.email}</span>
              <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Joined {(u.created_at || '').slice(0, 10)}</span>
              {u.onboarding_completed && <span className="flex items-center gap-1.5 text-green-300"><CheckCircle className="w-3.5 h-3.5" /> Onboarded</span>}
            </div>
            <div className="flex gap-2 mt-3 text-[10px] uppercase">
              <span className="px-2 py-1 rounded bg-purple-500/20 text-purple-200">{u.subscription_plan || 'free'}</span>
              <span className="px-2 py-1 rounded bg-green-500/20 text-green-300">{u.subscription_status}</span>
              {u.is_admin && <span className="px-2 py-1 rounded bg-pink-500/20 text-pink-300">Admin</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Categories */}
        <div className="lg:col-span-2 bg-white/[0.03] border border-white/5 rounded-2xl p-6">
          <h3 className="text-white font-semibold mb-4">Onboarding Data ({data.categories.filter(c => c.data).length}/{data.categories.length} filled)</h3>
          <div className="space-y-3">
            {data.categories.map(cat => (
              <details key={cat.slug} className="bg-white/[0.02] border border-white/5 rounded-lg overflow-hidden">
                <summary className="px-4 py-3 cursor-pointer hover:bg-white/[0.03] flex items-center justify-between">
                  <span className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full" style={{ background: cat.color }} />
                    <span className="text-white font-medium text-sm">{cat.name}</span>
                  </span>
                  <span className={`text-[10px] uppercase ${cat.data ? 'text-green-300' : 'text-purple-200/40'}`}>
                    {cat.data ? 'Filled' : 'Empty'}
                  </span>
                </summary>
                {cat.data && (
                  <pre className="text-xs text-purple-100/70 bg-black/30 p-4 overflow-x-auto whitespace-pre-wrap break-words">
                    {JSON.stringify(cat.data, null, 2)}
                  </pre>
                )}
                {cat.questions && Object.keys(cat.questions).length > 0 && (
                  <div className="px-4 py-3 border-t border-white/5">
                    <p className="text-xs uppercase text-purple-200/50 mb-2">User Q&amp;A</p>
                    {Object.entries(cat.questions).map(([k, v]) => (
                      <div key={k} className="mb-2">
                        <p className="text-xs text-purple-300">{k}</p>
                        <p className="text-sm text-white/80">{v}</p>
                      </div>
                    ))}
                  </div>
                )}
              </details>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-5">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><Package className="w-4 h-4" /> Products ({data.products.length})</h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {data.products.length === 0 && <p className="text-purple-200/40 text-xs">None.</p>}
              {data.products.map(p => (
                <div key={p.id} className="text-xs bg-white/[0.02] rounded p-2">
                  <p className="text-white">{p.name}</p>
                  <p className="text-purple-200/50">{p.category} · {p.confirmation_status || 'have'}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-5">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><Bell className="w-4 h-4" /> Recent Notifs</h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {data.notifications.slice(0, 5).map(n => (
                <div key={n.id} className="text-xs bg-white/[0.02] rounded p-2">
                  <p className="text-white truncate">{n.title}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-5">
            <h3 className="text-white font-semibold mb-2">Admin Notes</h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-purple-200/30 focus:outline-none focus:border-purple-400/50"
              placeholder="Internal notes…"
              data-testid="admin-notes-textarea"
            />
            <button onClick={saveNotes} disabled={savingNotes} className="mt-2 w-full py-2 rounded-lg text-sm font-medium text-white" style={{ background: 'linear-gradient(145deg,#a855f7,#7c3aed)' }} data-testid="save-notes-btn">
              {savingNotes ? 'Saving…' : 'Save Notes'}
            </button>
          </div>

          <button onClick={() => navigate(`/skyportal/reports?user=${userId}`)} className="w-full py-3 rounded-xl text-sm font-medium text-white" style={{ background: 'linear-gradient(145deg,#ec4899,#be185d)' }}>
            Build Report →
          </button>
        </div>
      </div>
    </div>
  );
}
